/**
 * Adaptive Timeline Support Service
 *
 * Provides consequence-based task triage for students who need support
 * but aren't in crisis mode. Uses CrisisRescueService intelligence
 * with lighter thresholds appropriate for proactive assistance.
 *
 * UX Philosophy: Maximally Assistive + Strategically Transparent
 */

import { differenceInDays, parseISO } from 'date-fns';

export class AdaptiveTimelineSupportService {
  /**
   * Generate focused support plan for a specific category or all tasks
   * @param {Array} timeline - All timeline tasks
   * @param {string} category - Selected category ('essays', 'research', 'all', etc.)
   * @param {Date} currentDate - Current date for deadline calculations
   * @returns {Object} Categorized support plan with metrics
   */
  static generateFocusedSupport(timeline, category, currentDate = new Date()) {
    // Filter tasks based on category selection
    const relevantTasks = category === 'all'
      ? timeline.filter(t => !t.isCompleted)
      : this.filterByCategory(timeline, category).filter(t => !t.isCompleted);

    if (relevantTasks.length === 0) {
      return {
        isEmpty: true,
        category,
        message: category === 'all'
          ? "You're all caught up! Great work staying on track."
          : `No active ${category} tasks right now. You're in great shape for this area!`
      };
    }

    // Categorize tasks by support type
    const categorized = this.categorizeTasksBySupport(relevantTasks, currentDate);

    // Calculate metrics
    const metrics = this.calculateSupportMetrics(categorized);

    return {
      isEmpty: false,
      category,
      categorized,
      metrics,
      totalTasks: relevantTasks.length
    };
  }

  /**
   * Categorize tasks into support buckets
   * @param {Array} tasks - Tasks to categorize
   * @param {Date} currentDate - Current date
   * @returns {Object} { mustComplete, qualityMatters, safeToSimplify }
   */
  static categorizeTasksBySupport(tasks, currentDate) {
    const mustComplete = [];
    const qualityMatters = [];
    const safeToSimplify = [];

    tasks.forEach(task => {
      // Must Complete: High consequence tasks or immediate deadlines
      if (this.isMustComplete(task, currentDate)) {
        mustComplete.push({
          ...task,
          supportReason: this.getMustCompleteReason(task, currentDate)
        });
      }
      // Quality Matters: High MVV risk or important tasks
      else if (this.isQualityPriority(task)) {
        qualityMatters.push({
          ...task,
          supportReason: this.getQualityReason(task)
        });
      }
      // Safe to Simplify: Low MVV risk with quick versions available
      else if (this.isSafeToSimplify(task)) {
        safeToSimplify.push({
          ...task,
          supportReason: this.getSimplifyReason(task),
          timeSavings: this.calculateTimeSavings(task)
        });
      }
      // Default to quality matters if no clear category
      else {
        qualityMatters.push({
          ...task,
          supportReason: 'Give this your focused attention'
        });
      }
    });

    // Sort by priority within each category
    return {
      mustComplete: this.sortByUrgency(mustComplete, currentDate),
      qualityMatters: this.sortByPriority(qualityMatters),
      safeToSimplify: this.sortByTimeSavings(safeToSimplify)
    };
  }

  /**
   * Calculate support metrics for display
   * @param {Object} categorized - Categorized tasks
   * @returns {Object} Metrics summary
   */
  static calculateSupportMetrics(categorized) {
    const totalTimeSavings = categorized.safeToSimplify.reduce(
      (sum, task) => sum + (task.timeSavings || 0),
      0
    );

    return {
      mustCompleteCount: categorized.mustComplete.length,
      qualityMattersCount: categorized.qualityMatters.length,
      safeToSimplifyCount: categorized.safeToSimplify.length,
      totalTimeSavings: totalTimeSavings,
      totalTimeSavingsHours: (totalTimeSavings / 60).toFixed(1)
    };
  }

  /**
   * Filter timeline by category
   * @param {Array} timeline - All tasks
   * @param {string} category - Category to filter by
   * @returns {Array} Filtered tasks
   */
  static filterByCategory(timeline, category) {
    const categoryMap = {
      'essays': ['essay', 'Essay Development'],
      'research': ['research', 'College Discovery'],
      'testing': ['testing', 'Testing Strategy'],
      'applications': ['admin', 'Application Execution'],
      'financial': ['financial', 'Financial Planning'],
      'all': null // No filter
    };

    const categoryKeys = categoryMap[category.toLowerCase()] || [category];

    if (categoryKeys === null) {
      return timeline;
    }

    return timeline.filter(task =>
      categoryKeys.some(key =>
        task.category?.toLowerCase() === key.toLowerCase() ||
        task.category === key
      )
    );
  }

  // === Classification Methods ===

  /**
   * Check if task is must-complete
   * @private
   */
  static isMustComplete(task, currentDate) {
    // High incompletion cost
    if (task.incompletion_cost >= 0.8) return true;

    // Critical priority
    if (task.priority === 'critical') return true;

    // Due within 3 days
    if (task.scheduled_date || task.due_date) {
      const dueDate = parseISO(task.scheduled_date || task.due_date);
      const daysUntilDue = differenceInDays(dueDate, currentDate);
      if (daysUntilDue <= 3 && daysUntilDue >= 0) return true;
    }

    // Overdue
    if (task.isOverdue) return true;

    return false;
  }

  /**
   * Check if task requires quality focus
   * @private
   */
  static isQualityPriority(task) {
    // High MVV risk - quality really matters
    if (task.mvv_risk >= 0.6) return true;

    // High priority
    if (task.priority === 'high') return true;

    // High cognitive intensity
    if (task.cognitive_intensity === 'high') return true;

    // Optimal Version Exists
    if (task.optimal_version) return true;

    return false;
  }

  /**
   * Check if task is safe to simplify
   * @private
   */
  static isSafeToSimplify(task) {
    // Low MVV risk and has MVV option
    if (task.mvv_risk <= 0.4 && task.min_viable_version) return true;

    // Can auto-generate
    if (task.can_auto_generate || task.can_autogenerate === 'yes') return true;

    // Low cognitive intensity and not high priority
    if (task.cognitive_intensity === 'low' && task.priority !== 'high') return true;

    return false;
  }

  // === Reason Generators (Strategically Transparent) ===

  static getMustCompleteReason(task, currentDate) {
    if (task.isOverdue) {
      return `⚠️ ${task.daysOverdue} days overdue - needs immediate attention`;
    }

    if (task.incompletion_cost >= 0.9) {
      return '🚨 Critical consequences if not completed';
    }

    if (task.scheduled_date || task.due_date) {
      const dueDate = parseISO(task.scheduled_date || task.due_date);
      const daysUntilDue = differenceInDays(dueDate, currentDate);
      if (daysUntilDue === 0) return '⏰ Due today';
      if (daysUntilDue === 1) return '⏰ Due tomorrow';
      if (daysUntilDue <= 3) return `⏰ Due in ${daysUntilDue} days`;
    }

    if (task.priority === 'critical') {
      return '🔴 Critical priority task';
    }

    return 'Needs your attention first';
  }

  static getQualityReason(task) {
    if (task.mvv_risk >= 0.7 && task.optimal_version) {
      return '💎 Quality really matters here - take your time';
    }

    if (task.cognitive_intensity === 'high') {
      return '🧠 Complex task - needs focused attention';
    }

    if (task.college_name) {
      return `📚 ${task.college_name} values depth in this area`;
    }

    return 'Give this your focused attention';
  }

  static getSimplifyReason(task) {
    if (task.mvv_risk <= 0.3 && task.min_viable_version) {
      return `✨ Quick version is perfectly fine (low quality gap)`;
    }

    if (task.can_auto_generate) {
      return '🤖 Can be auto-generated quickly';
    }

    if (task.cognitive_intensity === 'low') {
      return '⚡ Simple task - use streamlined approach';
    }

    return 'Safe to use efficient approach';
  }

  // === Sorting Methods ===

  static sortByUrgency(tasks, currentDate) {
    return tasks.sort((a, b) => {
      // Overdue first
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;

      // Then by days until due
      const dateA = a.scheduled_date || a.due_date;
      const dateB = b.scheduled_date || b.due_date;

      if (dateA && dateB) {
        const daysA = differenceInDays(parseISO(dateA), currentDate);
        const daysB = differenceInDays(parseISO(dateB), currentDate);
        return daysA - daysB;
      }

      // Then by incompletion cost
      return (b.incompletion_cost || 0) - (a.incompletion_cost || 0);
    });
  }

  static sortByPriority(tasks) {
    const priorityOrder = { critical: 0, high: 1, medium: 2, normal: 3, low: 4 };
    return tasks.sort((a, b) => {
      const priorityA = priorityOrder[a.priority] ?? 3;
      const priorityB = priorityOrder[b.priority] ?? 3;
      if (priorityA !== priorityB) return priorityA - priorityB;

      // Secondary sort by MVV risk (higher = more quality focus needed)
      return (b.mvv_risk || 0) - (a.mvv_risk || 0);
    });
  }

  static sortByTimeSavings(tasks) {
    return tasks.sort((a, b) => (b.timeSavings || 0) - (a.timeSavings || 0));
  }

  /**
   * Calculate time savings for MVV tasks
   * @private
   */
  static calculateTimeSavings(task) {
    if (task.optimal_time_required && task.mvv_time_required) {
      return task.optimal_time_required - task.mvv_time_required;
    }

    // Default estimates based on task type
    if (task.can_auto_generate) return 90; // 1.5 hours
    if (task.cognitive_intensity === 'low') return 30; // 30 min

    return 60; // 1 hour default
  }
}

export default AdaptiveTimelineSupportService;
